import { IAssignee, IDeletedAssignee } from "@gtmhub/assignees/models";
import { IProgress } from "@gtmhub/core";
import { WorkflowStatus } from "@webapp/okrs/goals/models/goal-workflow.models";
import { IAlignmentTreeMetric } from "../models";

export const treeEvents = [
  "itemTitleClick",
  "itemRearrange",
  "showMoreClick",
  "toggleItemSupporters",
  "toggleItemSelf",
  "contextMenuOpen",
  "additionalOwnersOpen",
  "additionalOwnersClose",
  "zoom",
] as const;
export type TreeEvent = typeof treeEvents[number];

export type AdditionalOwnersOpenEventDatum = {
  additionalOwners: IAssignee[];
  top: number;
  left: number;
};

export type TreeEventListener = (evt: Event, ...args: unknown[]) => void;

export interface IItemRearrangeEventArgs {
  item: IItem;
  newParent: IItem;
}

export interface IZoomRange {
  min: number;
  max: number;
}

export interface ITreeMargin {
  left?: number;
  top?: number;
}

export interface ISvgSize {
  width: number;
  height: number;
}

export type ItemFilter = (item: IItem) => boolean;

export interface IItemRearrangeAllowed {
  start(item: IItem): boolean;
  end(item: IItem, newParent: IItem): boolean;
}

export const everyItem = (): boolean => true;

export type TreeViewMode = "compact" | "medium" | "relaxed";

export type ItemType = "goal" | "metric";

export interface IItemId {
  id: string;
  type: ItemType;
}

export interface ICrossContributorLink extends IItemId {
  direction: "inbound" | "outbound";
  contributionValue?: number;
  calculateContributionValue(metric: IAlignmentTreeMetric): number;
}

export interface IItemSession {
  id: string;
  color: string;
}

export interface IItemWorkflow {
  status: WorkflowStatus;
}

export interface IItemParent {
  type: ItemType;
  id: string;
  relation: "contribution" | "support";
}

export type OwnerToPreview = (IAssignee | IDeletedAssignee) & { index: number; iconsCount: number };

export interface IItem extends IItemId {
  title: string;
  attainment: IProgress;
  fullAggregatedAttainment?: IProgress;
  sortedOwner?: IAssignee | IDeletedAssignee;
  owners?: {
    preview: OwnerToPreview[];
    additional: (IAssignee | IDeletedAssignee)[];
  };
  session: IItemSession;
  private?: boolean;
  // a private goal not owned by current user; OR
  // when we do not know the owner
  obfuscated?: boolean;
  // the item is part of a non-draft workflow and the user is not admin; OR
  // the session is not open anymore
  locked?: boolean;
  workflow?: IItemWorkflow;
  // if any node is hidden, there will be a "show more" placeholder
  // for all hidden children
  hidden?: boolean;
  contributors: IItem[];
  supporters: IItem[];
  // links between contributors of different items
  crossContributorLinks: ICrossContributorLink[];
  inboundCrossContributorLinksAllowed?: boolean;
  supportersExpanded?: boolean;
  contributorSelfExpanded?: boolean;
  matchesFilter?: boolean;
  located?: boolean;
  highlighted?: boolean;
  pinned?: boolean;
  designScore?: { totalPoints: number };
  parent?: IItemParent;
  // indicates if the metric's progress is being updated from another metric
  sourceMetricId?: string;
  orderId?: number;
}
